import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Save } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import {
  listNotificationTemplates,
  updateNotificationTemplate,
} from "@/services/api/notifications";
import type { NotificationTemplate } from "@/services/api/types";

function TemplateCard({ template }: { template: NotificationTemplate }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(template.subject ?? "");
  const [body, setBody] = useState(template.body);

  const save = useMutation({
    mutationFn: () =>
      updateNotificationTemplate(template.id, {
        locale: template.locale,
        channel: template.channel,
        subject: subject || null,
        body,
      }),
    onSuccess: () => {
      toast.success(t("notifications.templateSaved"));
      void queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
      setEditing(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{template.event_type}</Badge>
            <Badge variant="outline">{template.locale}</Badge>
            <Badge variant="outline">{template.channel}</Badge>
          </div>
          {!editing ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("common.edit")}
            </Button>
          ) : null}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor={`subject-${template.id}`}>{t("notifications.subject")}</Label>
              <Input
                id={`subject-${template.id}`}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`body-${template.id}`}>{t("notifications.body")}</Label>
              <Textarea
                id={`body-${template.id}`}
                value={body}
                rows={4}
                onChange={(event) => setBody(event.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                <Save className="h-4 w-4" />
                {t("common.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => {
                setSubject(template.subject ?? "");
                setBody(template.body);
                setEditing(false);
              }}>
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {template.subject ? (
              <p className="text-sm font-medium">{template.subject}</p>
            ) : null}
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{template.body}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Admin: per-locale, per-channel notification template copy. */
export function NotificationTemplatesPage() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["notification-templates"],
    queryFn: () => listNotificationTemplates(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.notificationTemplates")}</h1>
        <p className="text-sm text-muted-foreground">{t("notifications.templatesSubtitle")}</p>
      </div>

      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState title={t("notifications.emptyTemplates")} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {query.data?.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  );
}
